import React, { type FC, type JSX, useContext, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { type TranslationFunction, useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { getSearchModeFromSearchParams } from '@/app/components/search/utils/query2.ts';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import PriceLoader from '@/app/components/search/components/Card/Loaders/Price';
import type { DealCard } from '../../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from '../../../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import PriceDetails from './PriceDetails';
import PriceBottom from './PriceBottom';
import { BadgesContainer, PriceContainer } from './styled';
import Badges from '../Badges';
import { decorateTaxes } from '../../utils/decorateTaxes';
import { decoratePriceAge } from '../../utils/getPriceDetails';
import { getPricePromotions } from '../../utils/getPricePromotions.tsx';
import { hasPackageSavingsEnabled } from '../../../../utils/productsResponse';
import { getPackageSavings } from '../../../Modal/Modals/PackageSavingsModal/getPackageSavings';

type PriceProps = {
  dealCard: DealCard;
  isOverlay: boolean;
  promotionTypes: PROMOTION_TYPES[];
  promotionType: PROMOTION_TYPES | null;
};

const PACKAGE_SAVINGS_THRESHOLD = 2;
const PackageSavingsModal = dynamic(() => import('../../../Modal/Modals/PackageSavingsModal'), { ssr: false });

const getTotalSavingsPercentageLabel = (totalSavingsPercentage: number, t: TranslationFunction): string => {
  const label = t('vacation.oss.package_savings.badge.label');
  return label.replace('__amount__', `${totalSavingsPercentage}`);
};

const Price: FC<PriceProps> = ({ dealCard, isOverlay, promotionTypes, promotionType }): JSX.Element => {
  const isClient = useIsClient();
  const { t } = useTranslation();
  const priceContainerRef = useRef<HTMLDivElement>(undefined);
  const { localization } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { isWaiting, isLoading, searchParams } = useContext(CardsContext);
  const { curiosityClickEvents } = useCuriosityManager();
  const { configValuesApp, lang } = localization;
  const [showPackageSavingsModal, setShowPackageSavingsModal] = useState(false);
  const { doublePriceFamilyEnabled } = configValuesApp;
  const searchMode = getSearchModeFromSearchParams(searchParams);
  const priceFoundShort = t('vacation.oss.cards.price_found_short');
  const priceFound = t('vacation.oss.cards.price_found');
  const isOverlayAndMobile = isOverlay && isMobile;
  const { age, taxesTotal, taxes, isPerPerson } = dealCard?.product?.rate?.price || {};
  const showPrice = !isWaiting && !isLoading && dealCard?.product?.accommodation;
  const priceContainerClassName = '';
  const ageText = decoratePriceAge(isOverlay ? priceFoundShort : priceFound, lang, age);
  const showPromotionBadge = !isOverlay && promotionTypes && promotionTypes.length > 0;
  const showTotalPrice = !isPerPerson || doublePriceFamilyEnabled;
  const packageSavingsEnabled = hasPackageSavingsEnabled(dealCard, localization);
  const totalSavingsPercentage = getPackageSavings(dealCard, localization)?.totalSavingsPercentage;
  const packageSavingsIsOverThreshold = packageSavingsEnabled && totalSavingsPercentage >= PACKAGE_SAVINGS_THRESHOLD;
  const totalSavingsPercentageLabel = packageSavingsIsOverThreshold
    ? getTotalSavingsPercentageLabel(totalSavingsPercentage, t)
    : null;

  const openPackageSavingsModal = () => {
    setShowPackageSavingsModal(true);
    curiosityClickEvents.packageSavingsModalOpen();
  };

  const callbacks = {
    [PROMOTION_TYPES.PACKAGE_SAVINGS]: { callback: openPackageSavingsModal },
  };

  const promotions = getPricePromotions({
    promotionTypes,
    localization,
    t,
    isMobile,
    totalSavingsPercentageLabel,
    callbacks,
    dealCard,
  });

  const taxesText = decorateTaxes({
    taxes: !isPerPerson ? taxesTotal : taxes,
    localization,
    searchMode,
    t,
  });

  if (!showPrice) {
    return (
      <PriceContainer isOverlay={isOverlay} isWaiting={isWaiting} style={{ maxWidth: '100%', minWidth: '0' }}>
        <PriceLoader isOverlay={isOverlay} />
      </PriceContainer>
    );
  }

  return (
    <PriceContainer
      ref={priceContainerRef}
      isOverlay={isOverlay}
      isWaiting={isWaiting}
      isOverlayAndMobile={isOverlayAndMobile}
      className={priceContainerClassName}
      hasAgeText={!!ageText}
      hasTaxesText={!!taxesText}
    >
      <BadgesContainer>
        {showPromotionBadge &&
          promotions.map((promotion) => (
            <Badges promotion={promotion} key={`${promotion.promotionType}-${promotion.text}`} />
          ))}
      </BadgesContainer>
      <PriceDetails
        isOverlay={isOverlay}
        dealCard={dealCard}
        promotionTypes={promotionTypes}
        promotionType={promotionType}
        showTotalPrice={showTotalPrice}
      />
      <PriceBottom
        isOverlay={isOverlay}
        ageText={ageText}
        taxesText={taxesText}
        dealCard={dealCard}
        showTotalPrice={showTotalPrice}
      />
      {isClient && packageSavingsEnabled && (
        <PackageSavingsModal
          isOpen={showPackageSavingsModal}
          onClose={() => setShowPackageSavingsModal(false)}
          dealCard={dealCard}
        />
      )}
    </PriceContainer>
  );
};

export default Price;
